import classNames from "classnames";
import React from "react";

import { Image } from "../../common/Image";
import { useMountEffect } from "../../hooks/useMountEffect";
import { BreakPoint } from "../../models/screen";
import { getViewportBreakpoint } from "../../utils/detectMobile";
import { CompatibilityPanel, ICompatibilityAccordionBlock } from "../models";

import styles from "./CompatibilityAccordion.module.scss";

interface IProps {
    cms: ICompatibilityAccordionBlock;
    contentPanels: CompatibilityPanel[];
}

function getIcon(
    useIcon: boolean | null,
    cms: ICompatibilityAccordionBlock,
    compatibility: boolean,
): React.JSX.Element | undefined {
    if (!useIcon) {
        return;
    }
    const imgName = compatibility ? "icon_comp" : "icon_incomp";
    const altText = compatibility ? "compatible" : "incompatibile";

    return <Image src={cms[`${imgName}`]?.url} alt={`${altText} icon`} />;
}

function buildPanel(
    panel: CompatibilityPanel,
    useIcon: boolean | null,
    cms: ICompatibilityAccordionBlock,
    activePanel: CompatibilityPanel | null,
    onPanelClick: (panel: CompatibilityPanel | null) => void,
): React.JSX.Element {
    const rowClassName = classNames({
        [styles.row]: true,
        [styles.rowIcon]: useIcon,
    });
    const triggerClassName = classNames({
        [styles.accordionPanelTrigger]: true,
        [styles.accordionPanelTriggerIsActive]: activePanel === panel,
    });

    const targetClassName = classNames({
        [styles.accordionPanelTarget]: true,
        [styles.compatibilityAccordionImage]:
            getViewportBreakpoint() < BreakPoint.LARGE,
        [styles.accordionPanelTargetIsActive]: activePanel === panel,
    });
    const isActivePanel = activePanel === panel ? true : false;
    return (
        <div className={rowClassName} key={panel.value.title}>
            <h3>
                <button
                    className={triggerClassName}
                    onClick={() => {
                        if (activePanel !== panel) {
                            onPanelClick(panel);
                        } else {
                            onPanelClick(null);
                        }
                    }}
                    id={`trigger_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                    aria-controls={`panel_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
                    aria-expanded={isActivePanel}
                >
                    {getIcon(useIcon, cms, panel.value.choice)}{" "}
                    {panel.value.title}
                </button>
            </h3>
            <div
                className={targetClassName}
                id={`panel_${panel.value.title.toLowerCase().replace(/\s/g, "")}`}
            >
                <div
                    dangerouslySetInnerHTML={{
                        __html: panel.value.description || "",
                    }}
                ></div>

                {getViewportBreakpoint() < BreakPoint.MEDIUM && (
                    <Image
                        src={panel.value.image?.url}
                        alt={panel.value.title}
                        lazy={false}
                        className=""
                    />
                )}
            </div>
        </div>
    );
}

export const CompatibilityAccordion = (props: IProps): React.ReactNode => {
    const [useIcon, setUseIcon] = React.useState<boolean | null>(false);
    const [activePanel, setActivePanel] =
        React.useState<CompatibilityPanel | null>(null);

    useMountEffect(() => {
        setUseIcon(props.cms?.use_icon);
        setActivePanel(props.contentPanels[0]);
    });

    return (
        <div className={styles.compatibilityAccordionContent}>
            <div
                className={styles.compatibilityAccordionHeader}
                dangerouslySetInnerHTML={{
                    __html: props.cms.compatibility_header,
                }}
            ></div>
            {getViewportBreakpoint() >= BreakPoint.MEDIUM && (
                <div
                    className={`${styles.compatibilityAccordionImage} ${styles.accordionPanelTarget} ${styles.accordionPanelTargetIsActive}`}
                >
                    {activePanel && (
                        <Image
                            lazy={false}
                            className=""
                            src={activePanel?.value.image.url}
                            alt={activePanel?.value.title}
                        />
                    )}
                </div>
            )}
            <div className={styles.compatibilityAccordionPanels}>
                {props.contentPanels.map((panel: CompatibilityPanel) => {
                    return buildPanel(
                        panel,
                        useIcon,
                        props.cms,
                        activePanel,
                        setActivePanel,
                    );
                })}
            </div>
            <div
                dangerouslySetInnerHTML={{
                    __html: props.cms.compatibility_footer || "",
                }}
            ></div>
        </div>
    );
};
